import React from "react";
import { Alert, Typography, IconButton } from "@mui/material";
import { AlertTriangle, X } from "lucide-react";
import { TimezoneAbbreviations } from "../common/Timezones";

interface TimezoneMismatchBannerProps {
  show: boolean;
  browserTimezone: string;
  userTimezone: string;
  onDismiss?: () => void;
  dismissible?: boolean;
}

const TimezoneMismatchBanner: React.FC<TimezoneMismatchBannerProps> = ({
  show,
  browserTimezone,
  userTimezone,
  onDismiss,
  dismissible = false,
}) => {
  if (!show) return null;

  const getShortAbbreviation = (timezone: string) => {
    const fullAbbrev = TimezoneAbbreviations[timezone];
    if (fullAbbrev) {
      return fullAbbrev.split(" - ")[0];
    }
    return timezone.split("/").pop() || timezone;
  };

  const browserTzAbbr = getShortAbbreviation(browserTimezone);
  const userTzAbbr = getShortAbbreviation(userTimezone);

  return (
    <Alert
      severity="warning"
      sx={{
        marginBottom: 2,
        borderRadius: 2,
        "& .MuiAlert-message": {
          width: "100%",
          padding: "8px 0",
        },
        "& .MuiAlert-action": {
          padding: 0,
          alignItems: "center",
        },
      }}
      icon={<AlertTriangle size={20} />}
      action={
        dismissible && onDismiss ? (
          <IconButton
            size="small"
            onClick={onDismiss}
            sx={{
              color: "warning.main",
              "&:hover": {
                backgroundColor: "warning.light",
              },
            }}
          >
            <X size={16} />
          </IconButton>
        ) : undefined
      }
    >
      <Typography variant="body2">
        <strong>Timezone mismatch:</strong> Your browser location (
        {browserTzAbbr}) doesn't match your settings ({userTzAbbr}). Please
        double check the calendar times.
      </Typography>
    </Alert>
  );
};

export default TimezoneMismatchBanner;
